import type { CSSProperties, ReactNode } from 'react';
import type { WrappedResponse, WrappedTitle } from '../../api/types';
import { LINES, lineNamedBy, type Line as RailLine } from '../lines';
import { Wordmark } from '../Wordmark';
import {
  Bars,
  Columns,
  EpisodeRail,
  FormatMix,
  GenreBars,
  Gauge,
  ScoreScale,
  Stamps,
  TasteMap,
  TicketStrip,
} from './Charts';
import { days, fixed, int, share, topPercent } from './format';
import { SplitFlap } from './SplitFlap';

/** Where in a stop's choreography each part comes in, in ms after the train stops. */
const T = { kick: 250, hero: 650, chart: 1500, line: 2100, sub: 2700 };

const at = (ms: number): CSSProperties => ({ ['--d' as string]: `${ms}ms` });

function Kick({ children }: { children: ReactNode }) {
  return (
    <p className="kick st" style={at(T.kick)}>
      {children}
    </p>
  );
}

function Hero({ children, unit, delay = T.hero }: { children: ReactNode; unit?: string; delay?: number }) {
  return (
    <div className="hero st" style={at(delay)}>
      {children}
      {unit && <span className="unit">{unit}</span>}
    </div>
  );
}

function Line({ children, delay = T.line }: { children: ReactNode; delay?: number }) {
  return (
    <p className="line st" style={at(delay)}>
      {children}
    </p>
  );
}

function Sub({ children, delay = T.sub }: { children: ReactNode; delay?: number }) {
  return (
    <p className="sub st" style={at(delay)}>
      {children}
    </p>
  );
}

function Pick({
  title,
  badge,
  tone = 'gem',
  delay = T.hero,
}: {
  title: WrappedTitle;
  /** The rider's score, pinned to the cover's corner. */
  badge?: string;
  tone?: 'gem' | 'odd' | 'love' | 'plain';
  delay?: number;
}) {
  return (
    <div className={`pick st ${tone}`} style={at(delay)}>
      <span className="cover">
        {title.cover_image_url ? <img src={title.cover_image_url} alt="" /> : <i className="nocover" />}
        <span className="glint" aria-hidden="true" />
        {badge && <span className="badge">{badge}</span>}
      </span>
      <b>{title.title}</b>
    </div>
  );
}

interface SlideProps {
  data: WrappedResponse;
  /** Depart from the boarding platform. */
  onDepart?: () => void;
  /** The rider's lines in riding order, for the stop that explains the order. */
  lines?: RailLine[];
}

export function BoardingSlide({ data, onDepart, stops }: SlideProps & { stops: number }) {
  return (
    <>
      <Kick>
        <Wordmark kana /> ・ <Wordmark />
      </Kick>
      <Hero delay={T.hero}>
        <span className="welcome">
          Welcome aboard,
          <br />
          <b>{data.profile.username}</b>
        </span>
      </Hero>
      <Line delay={1300}>
        We checked your anime list against {int(Math.round(data.baseline_users / 1000) * 1000)} other people's.
        <br />
        {stops} stops, one thing to learn at each.
      </Line>
      <div className="st" style={at(2000)}>
        <button className="go" onClick={onDepart}>
          DEPART ▶
        </button>
      </div>
    </>
  );
}

export function DepartureSlide({ data }: SlideProps) {
  const d = data.departure;
  return (
    <>
      <Kick>YOU HAVE WATCHED</Kick>
      <Hero unit="shows">
        <SplitFlap value={int(d.titles)} delay={T.hero} size="lg" />
      </Hero>
      <Gauge
        value={d.titles_percentile}
        left="WATCHED FEWER"
        right="WATCHED MORE"
        mid={`TYPICAL ・ ${int(d.titles_typical)}`}
        delay={T.chart}
      />
      <Line>
        {d.titles_percentile >= 50
          ? `That's more than ${topPercent(d.titles_percentile)}% of people here.`
          : `${topPercent(100 - d.titles_percentile)}% of people here have watched more.`}
      </Line>
      <Sub>
        That's {int(d.hours)} hours, about {days(d.hours)} days of non-stop anime.
      </Sub>
    </>
  );
}

export function HarshnessSlide({ data }: SlideProps) {
  const h = data.harshness!;
  const gap = Math.abs(h.delta);
  const unit =
    gap < 0.25
      ? "points from the crowd's average"
      : h.delta < 0
        ? 'points harsher than the crowd'
        : 'points kinder than the crowd';
  return (
    <>
      <Kick>HOW YOU SCORE</Kick>
      <Hero unit={unit}>
        <SplitFlap value={fixed(gap)} delay={T.hero} size="lg" tone={h.delta < 0 ? 'amber' : 'green'} />
      </Hero>
      <ScoreScale you={h.user_mean} them={h.community_mean} delay={T.chart} />
      <Line>
        {/* Most people score a little above the crowd's average, so scoring
            right on it still puts a rider on the tough side; say why. */}
        {h.percentile >= 50
          ? gap < 0.25
            ? `Most people score a bit above the average, so you're still tougher than ${topPercent(h.percentile)}% of people.`
            : `You're a tougher grader than ${topPercent(h.percentile)}% of people.`
          : `You're a kinder grader than ${topPercent(100 - h.percentile)}% of people.`}
      </Line>
      <Sub>Your average score against everyone else's, on the same {int(h.scored)} shows.</Sub>
    </>
  );
}

export function ObscuritySlide({ data }: SlideProps) {
  const o = data.obscurity!;
  const obscure = o.percentile >= 50;
  return (
    <>
      <Kick>YOUR TASTE</Kick>
      <Hero
        unit={
          obscure
            ? 'of people watch more mainstream shows than you'
            : 'of people watch more obscure shows than you'
        }
      >
        <SplitFlap
          value={`${topPercent(obscure ? o.percentile : 100 - o.percentile)}%`}
          delay={T.hero}
          size="lg"
        />
      </Hero>
      <Gauge
        value={o.percentile}
        left="MAINSTREAM"
        right="OFFBEAT"
        delay={T.chart}
      />
      <Line>
        The typical show on your list is on {int(o.median_members)} people's lists. For most people, that
        number is {int(o.typical_members)}.
      </Line>
    </>
  );
}

export function GemSlide({ data }: SlideProps) {
  const g = data.hidden_gem!;
  const typical = data.obscurity?.median_members;
  return (
    <>
      <Kick>YOUR HIDDEN GEM</Kick>
      <Pick title={g.title} badge={fixed(g.user_score)} />
      <Bars
        delay={T.chart}
        rows={[
          { label: 'THIS SHOW', value: g.members, unit: 'lists', accent: true },
          ...(typical ? [{ label: 'A TYPICAL SHOW ON YOUR LIST', value: typical, unit: 'lists' }] : []),
        ]}
      />
      <Line>
        You gave it {fixed(g.user_score)} out of 10, and {typical && g.members <= typical / 4 ? 'far fewer' : 'fewer'}{' '}
        people have found it than most of what you watch.
      </Line>
    </>
  );
}

export function DisagreementSlide({ data }: SlideProps) {
  const d = data.disagreement!;
  return (
    <>
      <Kick>YOU DISAGREED WITH EVERYONE</Kick>
      <Pick title={d.title} badge={fixed(d.user_score)} tone="odd" />
      <Hero delay={1300} unit="points below everyone else">
        <SplitFlap value={fixed(Math.abs(d.gap))} delay={1300} size="md" />
      </Hero>
      <ScoreScale you={d.user_score} them={d.community_score} delay={2100} />
      <Line delay={2800}>
        {d.top100
          ? "And it's one of the 100 most popular shows here."
          : `${int(d.members)} people have it on their list.`}
      </Line>
    </>
  );
}

export function HomeSlide({ data, lines = [] }: SlideProps) {
  const h = data.home!;
  const rows = (h.breakdown ?? [{ genre: h.genre, share: h.share }]).slice(0, 5);
  const first = lines[0];
  const own = lineNamedBy(h.genre);
  return (
    <>
      <Kick>YOUR FAVOURITE GENRE</Kick>
      <Hero>
        <SplitFlap value={h.genre} delay={T.hero} size="md" />
      </Hero>
      <GenreBars rows={rows} typical={h.typical_share} delay={T.chart} />
      <Line>
        {share(h.share)} of what you watch is {h.genre}.{' '}
        {h.percentile >= 50
          ? `You stick to your genre more than ${topPercent(h.percentile)}% of people.`
          : `You branch out more than ${topPercent(100 - h.percentile)}% of people.`}
      </Line>
      {first && (
        <Sub>
          {!own
            ? `${h.genre} has no line of its own, so you start with the biggest genre that does.`
            : lines.some((l) => l.slug === own)
              ? 'Your lines run in this order.'
              : `None of your picks run on the ${h.genre} line, so you start with the next biggest.`}{' '}
          <span className="chip" style={{ ['--c' as string]: first.color } as CSSProperties}>
            FIRST STOP ・ {first.kanji} {first.lineEn}
          </span>
        </Sub>
      )}
    </>
  );
}

export function DropsSlide({ data }: SlideProps) {
  const d = data.drops!;
  return (
    <>
      <Kick>WHAT YOU STOP WATCHING</Kick>
      <Hero unit="of shows dropped">
        <SplitFlap value={share(d.rate)} delay={T.hero} size="lg" />
      </Hero>
      <TicketStrip rate={d.rate} dropped={d.dropped} delay={T.chart} />
      <Line>
        {d.dropped === 0
          ? 'You never dropped a show. You finish what you start.'
          : `You dropped ${int(d.dropped)} ${d.dropped === 1 ? 'show' : 'shows'}. The typical person drops ${share(d.typical_rate)} of theirs.`}
      </Line>
      {d.quickest && (
        <Sub>
          Fastest exit: <b>{d.quickest.title.title}</b>, after {d.quickest.episodes}{' '}
          {d.quickest.episodes === 1 ? 'episode' : 'episodes'}.
        </Sub>
      )}
    </>
  );
}

// ---- the later stops (spec slides 3, 6, 8, 10-12, 14-16) ----

export function ScoreCardSlide({ data }: SlideProps) {
  const c = data.score_card!;
  return (
    <>
      <Kick>HOW YOU HAND OUT SCORES</Kick>
      <Hero unit="of what you score gets a 9 or a 10">
        <SplitFlap value={share(c.top_share)} delay={T.hero} size="lg" />
      </Hero>
      <Columns
        values={c.histogram}
        labels={c.histogram.map((_, i) => String(i + 1))}
        hot={(i) => i >= 8}
        delay={T.chart}
      />
      <Line>
        {c.tens === 0
          ? "You've never given a perfect 10."
          : `You've given out ${int(c.tens)} perfect ${c.tens === 1 ? '10' : '10s'}.`}{' '}
        The typical person gives 9s and 10s to {share(c.typical_top_share)} of what they score.
      </Line>
    </>
  );
}

export function LovedSlide({ data }: SlideProps) {
  const l = data.loved!;
  return (
    <>
      <Kick>YOU LOVED IT ANYWAY</Kick>
      <Pick title={l.title} badge={fixed(l.user_score)} tone="love" />
      <Hero delay={1300} unit="points above everyone else">
        <SplitFlap value={fixed(l.gap)} delay={1300} size="md" tone="green" />
      </Hero>
      <ScoreScale you={l.user_score} them={l.community_score} delay={2100} />
      <Line delay={2800}>Most people were lukewarm on it. You weren't.</Line>
    </>
  );
}

export function LongestSlide({ data }: SlideProps) {
  const l = data.longest!;
  return (
    <>
      <Kick>YOUR LONGEST RIDE</Kick>
      <Pick title={l.title} tone="plain" />
      <Hero delay={1300} unit="episodes of one show">
        <SplitFlap value={int(l.episodes)} delay={1300} size="md" />
      </Hero>
      <Gauge
        value={l.percentile}
        left="SHORTER"
        right="LONGER"
        mid={`TYPICAL ・ ${int(l.typical_episodes)} EPS`}
        delay={2100}
      />
      <Line delay={2800}>That's {int(l.hours)} hours on a single show.</Line>
    </>
  );
}

export function SkippedGenreSlide({ data, lines = [] }: SlideProps) {
  const g = data.skipped_genre!;
  const slug = lineNamedBy(g.genre);
  const line = slug ? LINES.find((l) => l.slug === slug) : undefined;
  const running = line ? lines.find((l) => l.slug === line.slug) : undefined;
  return (
    <>
      <Kick>THE GENRE YOU SKIP</Kick>
      <Hero>
        <SplitFlap value={g.genre} delay={T.hero} size="md" tone="white" />
      </Hero>
      <Bars
        delay={T.chart}
        rows={[
          { label: 'YOUR LIST', value: g.share, unit: '', text: share(g.share), accent: true },
          { label: 'A TYPICAL LIST', value: g.typical_share, unit: '', text: share(g.typical_share) },
        ]}
      />
      <Line>Most people's lists carry a lot more {g.genre} than yours does.</Line>
      {running && (
        <Sub>
          <span className="chip" style={{ ['--c' as string]: running.color } as CSSProperties}>
            {running.kanji} {running.lineEn} ・ PLATFORM {running.id}
          </span>{' '}
          still runs on your network, if you fancy a detour.
        </Sub>
      )}
    </>
  );
}

export function FormatsSlide({ data }: SlideProps) {
  const f = data.formats!;
  const tv = f.mix.find((m) => m.format === 'TV');
  return (
    <>
      <Kick>HOW YOU WATCH</Kick>
      <Hero unit="of your list is movies">
        <SplitFlap value={share(f.movie_share)} delay={T.hero} size="lg" />
      </Hero>
      <FormatMix mix={f.mix} delay={T.chart} />
      <Line>
        {f.movie_percentile >= 50
          ? `You watch more movies than ${topPercent(f.movie_percentile)}% of people.`
          : `${topPercent(100 - f.movie_percentile)}% of people watch more movies than you.`}
        {tv && ` TV series make up ${share(tv.share)} of your list.`}
      </Line>
    </>
  );
}

export function FingerprintSlide({ data }: SlideProps) {
  const f = data.fingerprint!;
  return (
    <>
      <Kick>YOUR LIST KEEPS COMING BACK TO</Kick>
      <Stamps tags={f.tags} delay={T.hero} />
      <Line delay={T.hero + f.tags.length * 450 + 500}>
        These come up on your list more than on other people's. The number shows how many times as often.
      </Line>
    </>
  );
}

export function OnBoardSlide({ data }: SlideProps) {
  const b = data.on_board!;
  const total = b.current + b.paused;
  const c = b.closest;
  return (
    <>
      <Kick>STILL ON BOARD</Kick>
      <Hero unit={`${total === 1 ? 'show' : 'shows'} on the go`}>
        <SplitFlap value={int(total)} delay={T.hero} size="lg" />
      </Hero>
      {c && <EpisodeRail progress={c.progress} episodes={c.episodes} delay={T.chart} />}
      <Line>
        {c ? (
          <>
            You're {c.episodes - c.progress} {c.episodes - c.progress === 1 ? 'episode' : 'episodes'} from the end
            of <b>{c.title.title}</b>.
          </>
        ) : (
          `${int(b.current)} watching, ${int(b.paused)} paused.`
        )}
      </Line>
      <Sub>
        {c && `${int(b.current)} watching, ${int(b.paused)} paused. `}
        The typical person has {int(b.typical)} on the go.
      </Sub>
    </>
  );
}

export function BacklogSlide({ data }: SlideProps) {
  const b = data.backlog!;
  const ratio = data.departure.hours > 0 ? b.hours / data.departure.hours : 0;
  return (
    <>
      <Kick>YOUR BACKLOG</Kick>
      <Hero unit={`${b.planned === 1 ? 'show' : 'shows'} you plan to watch`}>
        <SplitFlap value={int(b.planned)} delay={T.hero} size="lg" />
      </Hero>
      <Gauge value={b.percentile} left="SHORT LIST" right="LONG LIST" mid={`TYPICAL ・ ${int(b.typical)}`} delay={T.chart} />
      <Line>
        About {int(b.hours)} hours, or {days(b.hours)} days of non-stop anime.
      </Line>
      {ratio > 0 && (
        <Sub>
          {ratio >= 1
            ? `That's more than everything you've watched so far.`
            : `That's ${share(ratio)} of everything you've watched so far.`}
        </Sub>
      )}
    </>
  );
}

export function EraSlide({ data }: SlideProps) {
  const e = data.era!;
  const busiest = Math.max(...e.decades.map((d) => d.count));
  return (
    <>
      <Kick>YOUR ERA</Kick>
      <Hero unit="is when your typical show came out">
        <SplitFlap value={String(e.median_year)} delay={T.hero} size="lg" />
      </Hero>
      <Columns
        values={e.decades.map((d) => d.count)}
        labels={e.decades.map((d) => `${String(d.decade % 100).padStart(2, '0')}s`)}
        hot={(i) => e.decades[i]!.count === busiest}
        delay={T.chart}
      />
      <Line>
        Oldest: <b>{e.oldest.title.title}</b> ({e.oldest.year}). Newest: <b>{e.newest.title.title}</b> (
        {e.newest.year}).
      </Line>
    </>
  );
}

/** The last stop before the ticket: where the list sits among everyone's. */
export function MapSlide({ data }: SlideProps) {
  const m = data.taste_map!;
  const lean = m.side ? m.ends[m.side] : null;
  return (
    <>
      <Kick>YOU ARE HERE</Kick>
      <TasteMap map={m} delay={T.hero} />
      <Line delay={T.line + 400}>
        {/* A list can lean one way and still sit nearer the middle than most,
            so the two halves are joined to say which it is. */}
        {lean && m.percentile >= 50
          ? `Your list pulls towards ${lean.join(' and ')}. You're further out than ${topPercent(m.percentile)}% of people.`
          : lean
            ? `Your list leans a little towards ${lean.join(' and ')}, but you're closer to the middle than ${topPercent(100 - m.percentile)}% of people.`
            : `You sit near the middle, where most lists overlap. You're closer in than ${topPercent(100 - m.percentile)}% of people.`}
      </Line>
      <Sub delay={T.sub + 400}>
        Each dot is a group of people with similar lists. The lit ones are your neighbours,{' '}
        {share(m.neighbours)} of everyone.
      </Sub>
    </>
  );
}
